import { z } from "zod";
import { catalogFlagsSchema } from "./catalog-flags.js";
export { catalogFlagsSchema, resolveCatalogFlags } from "./catalog-flags.js";

export const requestIdSchema = z.string().min(8).max(128);

export const errorSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.record(z.string(), z.unknown()).optional()
});

export const envelopeSchema = <T extends z.ZodType>(data: T) =>
  z.object({ ok: z.literal(true), data, error: z.null(), requestId: requestIdSchema });

export const errorEnvelopeSchema = z.object({
  ok: z.literal(false),
  data: z.null(),
  error: errorSchema,
  requestId: requestIdSchema
});

const optionalText = z.string().nullable();

const artworkTextMarksSchema = z.array(z.enum(["strong", "emphasis"])).max(2);

export const artworkInlineSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("text"), text: z.string().min(1), marks: artworkTextMarksSchema }),
  z.object({ type: z.literal("break") }),
  z.object({
    type: z.literal("link"),
    text: z.string().min(1),
    href: z.string().min(1),
    marks: artworkTextMarksSchema
  })
]);

const artworkInlineContentSchema = z.array(artworkInlineSchema);

export const artworkContentBlockSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("heading"),
    level: z.union([z.literal(2), z.literal(3)]),
    text: z.string().min(1)
  }),
  z.object({ type: z.literal("paragraph"), content: artworkInlineContentSchema.min(1) }),
  z.object({ type: z.literal("quote"), content: artworkInlineContentSchema.min(1) }),
  z.object({
    type: z.literal("list"),
    ordered: z.boolean(),
    items: z.array(artworkInlineContentSchema.min(1)).min(1)
  })
]);

export const artworkSummarySchema = z.object({
  id: z.string().min(1),
  seq: z.number().int().nullable(),
  title: optionalText,
  orientation: optionalText,
  width: optionalText,
  height: optionalText,
  medium: optionalText,
  paper: optionalText,
  excerpt: optionalText,
  categoryPrimary: z.string(),
  sale: optionalText,
  sold: optionalText,
  image: z.object({ md: z.string().url(), lg: z.string().url(), xl: z.string().url() })
});

const artworkDetailFieldsSchema = artworkSummarySchema.extend({
  description: optionalText,
  categorySecondary: z.string(),
  subjectTags: optionalText,
  styleTraits: optionalText,
  environmentalMoods: optionalText,
  visualElements: optionalText,
  style: optionalText,
  artCreated: optionalText,
  texture: optionalText,
  artCondition: optionalText,
  conditionDetails: optionalText,
  genre: optionalText,
  movement: optionalText,
  technique: optionalText,
  influence: optionalText,
  presentationContext: optionalText,
  marketPositioning: optionalText,
  publish: optionalText,
  imageStorage: z.string()
});

export const artworkDetailSchema = artworkDetailFieldsSchema.extend({
  descriptionBlocks: z.array(artworkContentBlockSchema)
});

export const adminArtworkSummarySchema = artworkSummarySchema.extend({
  idx: optionalText,
  storage: optionalText,
  publish: optionalText,
  created: optionalText
});

export const adminArtworkDetailSchema = artworkDetailFieldsSchema.extend({
  idx: optionalText,
  ratio: optionalText,
  storage: optionalText,
  pressType: optionalText,
  tint: optionalText,
  created: optionalText,
  edited: optionalText,
  excerptBlocks: z.array(artworkContentBlockSchema),
  descriptionBlocks: z.array(artworkContentBlockSchema),
  styleBlocks: z.array(artworkContentBlockSchema)
});

export const catalogQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(60).default(15),
  q: z.string().trim().max(120).default(""),
  orientation: z.enum(["Landscape", "Portrait"]).optional(),
  medium: z.string().trim().max(80).optional(),
  sort: z.enum(["seq-asc", "seq-desc", "title-asc", "title-desc", "edited-desc"]).default("seq-asc")
});

export const catalogPageSchema = z.object({
  items: z.array(artworkSummarySchema),
  pagination: z.object({
    page: z.number().int(),
    pageSize: z.number().int(),
    totalItems: z.number().int(),
    totalPages: z.number().int()
  }),
  filters: z.object({ orientations: z.array(z.string()), mediums: z.array(z.string()) })
});

export const adminCatalogPageSchema = catalogPageSchema.extend({
  items: z.array(adminArtworkSummarySchema)
});

export const contentBlockSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("heading"), level: z.number().int().min(2).max(3), text: z.string() }),
  z.object({ type: z.literal("paragraph"), text: z.string() }),
  z.object({ type: z.literal("quote"), text: z.string() }),
  z.object({ type: z.literal("list"), items: z.array(z.string()) }),
  z.object({ type: z.literal("image"), src: z.string().url(), alt: z.string() }),
  z.object({ type: z.literal("callout"), title: z.string(), lines: z.array(z.string()) })
]);

export const pageContentSchema = z.object({
  key: z.string(),
  slug: z.string(),
  title: z.string(),
  subtitle: z.string().nullable(),
  description: z.string(),
  blocks: z.array(contentBlockSchema),
  seo: z.object({
    title: z.string(),
    description: z.string(),
    socialImage: z.string().url().nullable()
  }),
  schemaVersion: z.number().int(),
  revision: z.number().int(),
  updatedAt: z.string()
});

export const navigationSchema = z.object({
  items: z.array(z.object({ label: z.string(), href: z.string(), order: z.number().int() }))
});

export const firebaseWebConfigSchema = z.object({
  apiKey: z.string().min(1),
  authDomain: z.string().min(1),
  projectId: z.string().min(1),
  storageBucket: z.string().optional(),
  messagingSenderId: z.string().optional(),
  appId: z.string().min(1),
  measurementId: z.string().optional()
});

export const currentUserSchema = z.object({
  userId: z.string().uuid(),
  displayName: z.string(),
  email: z.string().email(),
  roles: z.array(z.string()),
  permissions: z.array(z.string()),
  sessionId: z.string().uuid()
});

export type ArtworkSummary = z.infer<typeof artworkSummarySchema>;
export type ArtworkInline = z.infer<typeof artworkInlineSchema>;
export type ArtworkContentBlock = z.infer<typeof artworkContentBlockSchema>;
export type ArtworkDetail = z.infer<typeof artworkDetailSchema>;
export type AdminArtworkSummary = z.infer<typeof adminArtworkSummarySchema>;
export type AdminArtworkDetail = z.infer<typeof adminArtworkDetailSchema>;
export type CatalogPage = z.infer<typeof catalogPageSchema>;
export type AdminCatalogPage = z.infer<typeof adminCatalogPageSchema>;
export type CatalogQuery = z.infer<typeof catalogQuerySchema>;
export type PageContent = z.infer<typeof pageContentSchema>;
export type Navigation = z.infer<typeof navigationSchema>;
export type FirebaseWebConfig = z.infer<typeof firebaseWebConfigSchema>;
export type CurrentUser = z.infer<typeof currentUserSchema>;

export const syntheticFoundationJobName = "eaf.foundation.validate.v1" as const;

export const syntheticFoundationJobSchema = z.object({
  contractVersion: z.literal(1),
  validationId: z.string().uuid(),
  correlationId: requestIdSchema,
  expectedDigest: z.string().regex(/^[a-f0-9]{64}$/),
  retryOnce: z.boolean().default(false),
  forceFailure: z.boolean().default(false)
});

export const syntheticFoundationResultSchema = z.object({
  contractVersion: z.literal(1),
  validationId: z.string().uuid(),
  digest: z.string().regex(/^[a-f0-9]{64}$/),
  valid: z.literal(true)
});

export type SyntheticFoundationJob = z.infer<typeof syntheticFoundationJobSchema>;
export type SyntheticFoundationResult = z.infer<typeof syntheticFoundationResultSchema>;

export const idempotencyKeySchema = z
  .string()
  .trim()
  .min(8)
  .max(128)
  .regex(/^[A-Za-z0-9._:-]+$/);

export const createDraftSchema = z.object({
  originalFilename: z.string().trim().min(1).max(255),
  photographicSequence: z.number().int().positive().max(2_147_483_647).optional(),
  sequenceCorrectionReason: z.string().trim().min(8).max(500).optional()
});

export const draftPatchSchema = z.object({
  catalogFlags: catalogFlagsSchema.optional(),
  expectedVersion: z.number().int().positive(),
  patch: z
    .record(z.string().min(1).max(120), z.unknown())
    .refine(
      (value) => !Object.keys(value).some((key) => ["__proto__", "constructor"].includes(key))
    )
});

export const uploadIntentRequestSchema = z.object({
  originalFilename: z.string().trim().min(1).max(255),
  mediaType: z.enum(["image/jpeg", "image/png", "image/webp"]),
  maximumBytes: z
    .number()
    .int()
    .min(1)
    .max(40 * 1024 * 1024),
  checksumSha256Base64: z.string().regex(/^[A-Za-z0-9+/]{43}=$/),
  checksumMd5Base64: z.string().regex(/^[A-Za-z0-9+/]{22}==$/),
  side: z.enum(["front", "back"]).default("front")
});

export const uploadIntentSchema = z.object({
  uploadIntentId: z.string().uuid(),
  artworkId: z.string().uuid(),
  assetId: z.string().uuid(),
  objectKey: z.string().min(1),
  uploadUrl: z.string().url(),
  requiredHeaders: z.record(z.string(), z.string()),
  expiresAt: z.string().datetime(),
  maximumBytes: z.number().int().positive()
});

export const versionedCommandSchema = z.object({
  expectedVersion: z.number().int().positive()
});

export const artworkStatusDecisionSchema = z.object({
  expectedVersion: z.number().int().positive(),
  publicationState: z.enum(["unpublished", "preview", "published", "withdrawn"]),
  commerceState: z.enum(["not_offered", "available", "reserved", "sold", "not_for_sale"]),
  catalogMaturity: z.enum(["intake", "core", "developed", "scholarly"]),
  preservationState: z.string().trim().min(1).max(80),
  fundraisingState: z.string().trim().min(1).max(80),
  placementState: z.string().trim().min(1).max(80),
  reason: z.string().trim().min(3).max(1_000)
});

export const aiRequestSchema = z.object({
  methodKey: z.literal("eaf-vision-once-v1").default("eaf-vision-once-v1"),
  imageDetail: z.enum(["high", "original"]).default("high")
});

export const assistedFieldSchema = z.enum([
  "title",
  "objectType",
  "materials",
  "technique",
  "support",
  "subject",
  "style",
  "publicDiscoveryTags",
  "description",
  "publicExcerpt",
  "visibleCondition",
  "missingInformation",
  "aboutThisWork",
  "aCloserLook",
  "displayPossibilities",
  "fromTheCollection"
]);

export const targetedAiRequestSchema = z.object({
  fields: z.array(assistedFieldSchema).min(1).max(4)
});

export const suggestionDecisionSchema = z.object({
  decision: z.enum(["accept", "edit_accept", "reject", "defer", "replace_manual"]),
  value: z.unknown().optional(),
  reason: z.string().trim().min(3).max(1_000)
});

export const vocabularyProposalSchema = z.object({
  artworkId: z.string().uuid().optional(),
  schemeKey: z.string().trim().min(1).max(80),
  label: z.string().trim().min(1).max(180),
  definition: z.string().trim().max(2_000).optional(),
  parentConceptId: z.string().uuid().optional(),
  externalAuthorityUri: z.string().url().optional(),
  evidence: z.record(z.string(), z.unknown()).default({})
});

export const vocabularyReviewSchema = z.object({
  decision: z.enum(["approve", "reject", "withdraw"]),
  note: z.string().trim().min(3).max(1_000)
});

export const evaluationRecordSchema = z.object({
  evaluationRunId: z.string().uuid(),
  caseId: z.string().uuid(),
  passed: z.boolean(),
  metrics: z.record(z.string(), z.union([z.number(), z.string(), z.boolean(), z.null()]))
});

export const migrationRequestSchema = z.object({
  mode: z.enum(["dry_run", "pilot_copy"]),
  assetIds: z.array(z.string().uuid()).max(12).default([])
});

export const styleFamilyCreateSchema = z.object({
  name: z.string().trim().min(1).max(120),
  description: z.string().trim().max(2_000).default("")
});

export const styleFamilyUpdateSchema = styleFamilyCreateSchema.extend({
  expectedVersion: z.number().int().positive(),
  state: z.enum(["active", "retired"]).default("active")
});

export const artworkStyleAssignmentSchema = z.object({
  styleFamilyIds: z.array(z.string().uuid()).max(30),
  curatorNote: z.string().trim().max(2_000).default("")
});

export const exhibitionCreateSchema = z.object({
  title: z.string().trim().min(1).max(180),
  purpose: z.string().trim().max(4_000).default(""),
  curatorNotes: z.string().trim().max(10_000).default("")
});

export const exhibitionUpdateSchema = exhibitionCreateSchema.extend({
  expectedVersion: z.number().int().positive(),
  artworkIds: z.array(z.string().uuid()).max(100)
});

export const curatorDraftSummarySchema = z.object({
  artworkId: z.string().uuid(),
  legacyCatalogId: z.string().nullable(),
  photographicSequence: z.number().int().nullable(),
  workflowState: z.string(),
  publicationState: z.string(),
  commerceState: z.string(),
  catalogMaturity: z.string(),
  preservationState: z.string(),
  fundraisingState: z.string(),
  placementState: z.string(),
  version: z.number().int().positive(),
  title: z.string().nullable(),
  updatedAt: z.string()
});

export const curatorDashboardSchema = z.object({
  drafts: z.array(curatorDraftSummarySchema),
  processing: z.array(
    z.object({
      taskId: z.string().uuid(),
      taskType: z.string(),
      state: z.string(),
      artworkId: z.string().uuid().nullable(),
      updatedAt: z.string(),
      terminalReason: z.string().nullable()
    })
  ),
  counts: z.object({
    drafts: z.number().int().nonnegative(),
    review: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative()
  })
});

export const curatorAssetSchema = z.object({
  assetId: z.string().uuid(),
  kind: z.string(),
  side: z.string(),
  role: z.string(),
  state: z.string(),
  verification: z.string(),
  mediaType: z.string().nullable(),
  bytes: z.number().int().nullable(),
  width: z.number().int().nullable(),
  height: z.number().int().nullable(),
  url: z.string().url().nullable()
});

export const curatorSuggestionSchema = z.object({
  suggestionId: z.string().uuid(),
  aiRunId: z.string().uuid(),
  assistanceRequestId: z.string().uuid().nullable(),
  fieldKey: z.string(),
  value: z.unknown(),
  confidence: z.number().nullable(),
  rationale: z.string().nullable(),
  state: z.string(),
  createdAt: z.string()
});

export const curatorAiRunSchema = z.object({
  aiRunId: z.string().uuid(),
  parentAiRunId: z.string().uuid().nullable(),
  taskType: z.string(),
  state: z.string(),
  inputImageCount: z.number().int().min(0).max(1),
  estimatedCostUsd: z.number().nullable(),
  requestedFields: z.array(assistedFieldSchema),
  createdAt: z.string(),
  completedAt: z.string().nullable()
});

export const styleFamilySchema = z.object({
  styleFamilyId: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  assigned: z.boolean(),
  curatorNote: z.string(),
  version: z.number().int().positive(),
  artworkCount: z.number().int().nonnegative()
});

export const relatedArtworkSchema = z.object({
  artworkId: z.string().uuid(),
  photographicSequence: z.number().int().nullable(),
  title: z.string().nullable(),
  sharedTraits: z.array(z.string()).max(12)
});

export const exhibitionSummarySchema = z.object({
  exhibitionId: z.string().uuid(),
  title: z.string(),
  purpose: z.string(),
  curatorNotes: z.string(),
  state: z.string(),
  version: z.number().int().positive(),
  artworkCount: z.number().int().nonnegative(),
  includesArtwork: z.boolean(),
  updatedAt: z.string()
});

export const exhibitionDetailSchema = exhibitionSummarySchema.extend({
  artworks: z.array(
    z.object({
      artworkId: z.string().uuid(),
      photographicSequence: z.number().int().nullable(),
      title: z.string().nullable(),
      position: z.number().int().positive(),
      curatorNote: z.string()
    })
  )
});

export const exhibitionWorkspaceSchema = z.object({
  exhibitions: z.array(exhibitionDetailSchema),
  artworks: z.array(
    z.object({
      artworkId: z.string().uuid(),
      photographicSequence: z.number().int().nullable(),
      title: z.string().nullable()
    })
  )
});

export const curatorHistoryEntrySchema = z.object({
  type: z.enum(["revision", "state", "command"]),
  label: z.string(),
  detail: z.string().nullable(),
  occurredAt: z.string()
});

export const curatorArtworkSchema = z.object({
  artworkId: z.string().uuid(),
  legacyCatalogId: z.string().nullable(),
  photographicSequence: z.number().int().nullable(),
  workflowState: z.string(),
  publicationState: z.string(),
  commerceState: z.string(),
  catalogMaturity: z.string(),
  preservationState: z.string(),
  fundraisingState: z.string(),
  placementState: z.string(),
  version: z.number().int().positive(),
  document: z.record(z.string(), z.unknown()),
  assets: z.array(curatorAssetSchema),
  suggestions: z.array(curatorSuggestionSchema),
  aiRuns: z.array(curatorAiRunSchema),
  styleFamilies: z.array(styleFamilySchema),
  relatedWorks: z.array(relatedArtworkSchema),
  exhibitions: z.array(exhibitionSummarySchema),
  history: z.array(curatorHistoryEntrySchema),
  updatedAt: z.string()
});

export const curatorGovernanceSchema = z.object({
  methods: z.array(
    z.object({
      methodProfileId: z.string().uuid(),
      methodKey: z.string(),
      semanticVersion: z.string(),
      status: z.string(),
      createdAt: z.string()
    })
  ),
  models: z.array(
    z.object({
      modelProfileId: z.string().uuid(),
      profileKey: z.string(),
      semanticVersion: z.string(),
      modelIdentifier: z.string(),
      status: z.string(),
      inputPerMillion: z.number().nullable(),
      outputPerMillion: z.number().nullable(),
      effectiveAt: z.string().nullable()
    })
  ),
  evaluations: z.array(
    z.object({
      evaluationRunId: z.string().uuid(),
      state: z.string(),
      createdAt: z.string(),
      resultCount: z.number().int(),
      passedCount: z.number().int()
    })
  ),
  vocabularyProposals: z.array(
    z.object({
      proposalId: z.string().uuid(),
      schemeKey: z.string(),
      proposedLabel: z.string(),
      state: z.string(),
      proposedAt: z.string(),
      reviewedAt: z.string().nullable()
    })
  )
});

const jobBaseSchema = z.object({
  contractVersion: z.literal(1),
  taskId: z.string().uuid(),
  artworkId: z.string().uuid(),
  correlationId: requestIdSchema,
  idempotencyKey: idempotencyKeySchema
});

export const assetProcessJobName = "eaf.asset.process.v1" as const;
export const derivativeVerifyJobName = "eaf.asset.derivative.verify.v1" as const;
export const sourceReconcileJobName = "eaf.asset.source.reconcile.v1" as const;
export const migrationCopyJobName = "eaf.asset.migration.copy.v1" as const;
export const aiObservationJobName = "eaf.ai.observation.v1" as const;
export const aiTextDerivationJobName = "eaf.ai.text-derivation.v1" as const;
export const aiTargetedAssistanceJobName = "eaf.ai.targeted-assistance.v1" as const;
export const providerEventJobName = "eaf.ai.provider-event.v1" as const;
export const evaluationAggregateJobName = "eaf.ai.evaluation.aggregate.v1" as const;

export const assetProcessJobSchema = jobBaseSchema.extend({
  uploadIntentId: z.string().uuid(),
  assetId: z.string().uuid()
});
export const derivativeVerifyJobSchema = jobBaseSchema.extend({
  sourceAssetId: z.string().uuid(),
  derivativeAssetIds: z.array(z.string().uuid()).min(1).max(8)
});
export const sourceReconcileJobSchema = jobBaseSchema.extend({
  uploadIntentId: z.string().uuid(),
  sourceAssetId: z.string().uuid()
});
export const migrationCopyJobSchema = jobBaseSchema.extend({
  migrationRunId: z.string().uuid(),
  migrationItemId: z.string().uuid()
});
export const aiObservationJobSchema = jobBaseSchema.extend({
  aiRunId: z.string().uuid(),
  analysisAssetId: z.string().uuid(),
  imageDetail: z.enum(["high", "original"])
});
export const aiTextDerivationJobSchema = jobBaseSchema.extend({
  aiRunId: z.string().uuid(),
  observationArtifactId: z.string().uuid()
});
export const aiTargetedAssistanceJobSchema = jobBaseSchema.extend({
  aiRunId: z.string().uuid(),
  assistanceRequestId: z.string().uuid(),
  observationArtifactId: z.string().uuid(),
  requestedFields: z.array(assistedFieldSchema).min(1).max(4)
});
export const providerEventJobSchema = z.object({
  contractVersion: z.literal(1),
  taskId: z.string().uuid(),
  providerEventId: z.string().uuid(),
  correlationId: requestIdSchema,
  idempotencyKey: idempotencyKeySchema
});
export const evaluationAggregateJobSchema = z.object({
  contractVersion: z.literal(1),
  taskId: z.string().uuid(),
  evaluationRunId: z.string().uuid(),
  correlationId: requestIdSchema,
  idempotencyKey: idempotencyKeySchema
});

export type CreateDraft = z.infer<typeof createDraftSchema>;
export type DraftPatch = z.infer<typeof draftPatchSchema>;
export type ArtworkStatusDecision = z.infer<typeof artworkStatusDecisionSchema>;
export type UploadIntentRequest = z.infer<typeof uploadIntentRequestSchema>;
export type UploadIntent = z.infer<typeof uploadIntentSchema>;
export type AiRequest = z.infer<typeof aiRequestSchema>;
export type AssistedField = z.infer<typeof assistedFieldSchema>;
export type TargetedAiRequest = z.infer<typeof targetedAiRequestSchema>;
export type SuggestionDecision = z.infer<typeof suggestionDecisionSchema>;
export type VocabularyProposal = z.infer<typeof vocabularyProposalSchema>;
export type VocabularyReview = z.infer<typeof vocabularyReviewSchema>;
export type EvaluationRecord = z.infer<typeof evaluationRecordSchema>;
export type MigrationRequest = z.infer<typeof migrationRequestSchema>;
export type StyleFamilyCreate = z.infer<typeof styleFamilyCreateSchema>;
export type StyleFamilyUpdate = z.infer<typeof styleFamilyUpdateSchema>;
export type ArtworkStyleAssignment = z.infer<typeof artworkStyleAssignmentSchema>;
export type ExhibitionCreate = z.infer<typeof exhibitionCreateSchema>;
export type ExhibitionUpdate = z.infer<typeof exhibitionUpdateSchema>;
export type CuratorDashboard = z.infer<typeof curatorDashboardSchema>;
export type CuratorArtwork = z.infer<typeof curatorArtworkSchema>;
export type CuratorGovernance = z.infer<typeof curatorGovernanceSchema>;
export type ExhibitionWorkspace = z.infer<typeof exhibitionWorkspaceSchema>;
export type AssetProcessJob = z.infer<typeof assetProcessJobSchema>;
export type DerivativeVerifyJob = z.infer<typeof derivativeVerifyJobSchema>;
export type SourceReconcileJob = z.infer<typeof sourceReconcileJobSchema>;
export type MigrationCopyJob = z.infer<typeof migrationCopyJobSchema>;
export type AiObservationJob = z.infer<typeof aiObservationJobSchema>;
export type AiTextDerivationJob = z.infer<typeof aiTextDerivationJobSchema>;
export type AiTargetedAssistanceJob = z.infer<typeof aiTargetedAssistanceJobSchema>;
export type ProviderEventJob = z.infer<typeof providerEventJobSchema>;
export type EvaluationAggregateJob = z.infer<typeof evaluationAggregateJobSchema>;
export * from "./title-suggestions.js";
export * from "./storytelling.js";
